import { AfterViewInit, Component, Input, OnDestroy, SimpleChanges } from '@angular/core';
import { environment } from '@eview/core';
import { AuthHelpers } from '@eview/core/auth';
import { Permission } from '@eview/core/auth/permission';
import { GeoJsonObject } from '@eview/core/domain/post/geo-json';
import { Marker, Position } from '@eview/core/models/map';
import {
  EMapActions,
  SetMapDefaults,
  SetMapRegion,
  SimulateUserClickedMap,
  UserClickedMap,
  UserClickedMarker,
  UserClickedCommentMap
} from '@eview/core/store/actions/map.actions';
import { selectMap } from '@eview/core/store/selectors/map.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as happen from 'happen';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import * as lodash from 'lodash';
import { Subscription } from 'rxjs';
import * as shp from 'shpjs';
import * as mapIcons from './icons';

import { selectTenant } from '@eview/core/store/selectors/tenant.selector';

const MAP_INITIAL_CENTER: L.LatLngExpression = [0, 0];
const MAP_INITIAL_ZOOM: number = 1;
const MAP_ZOOM_POSITION: L.ControlPosition = 'bottomleft';
const MAP_LAYER_POSITION: L.ControlPosition = 'bottomleft';
const MAP_URL_TEMPLATE: string =
  'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const MAP_ZOOM_MAX: number = 19;
const MAP_ATTRIBUTION: string =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const CREATE_MAP_ID = 'post-create-map';
const VIEW_MAP_ID = 'map-view';
const MOBILE_MAP_ID = 'map-view-mobile';
const MAP_VIEW_DETAIL = 'map-view-detail';
const MAP_VIEW_GET_ALERTS = 'map-view-get-alerts'
const LAT_DEVIATION = 0.90;
const LON_DEVIATION = 1.20;
const DEFAULT_WIDTH = 1367;

@Component({
  selector: 'eview-map',
  styleUrls: ['map.componets.scss'],
  template: `
    <div class="map-container" id="map-container" style="height: 100%;">
      <div class="map-frame" style="height: 100%;">
        <div [id]="mapId" style="height: 100%; z-index: 0;" class="font-regular"></div>
      </div>
    </div>
  `
})
export class MapComponent implements AfterViewInit, OnDestroy {

  @Input() disabled: boolean = false;

  @Input() marker: Position;

  @Input() markersArray: Marker[];

  @Input() getMarkersFromStore: boolean = false;

  @Input() mapId: string;

  private layers: any = [];

  private map: L.Map;

  private mapLayerControl: L.Control.Layers;

  private initiated: boolean = false;

  private userCanClick: boolean = false;

  private userCursorMarker: L.Marker = null;

  private userCursorProperties: any;

  private markers: Marker[];

  private mapMarkersCluster: L.MarkerClusterGroup;

  private clickedMapMarker: L.Marker;

  private initialZoom: number = null;

  private subs: Subscription;

  private isMapClick: boolean = false;

  layerGroup: L.LayerGroup;

  AuthHelpers = AuthHelpers;

  ListenerHandle: any;

  markerHandler: any;

  mapMarkerHandler: any;

  Permission = Permission;
  store: Store<AppState>;

  tenant: any;
  tenantName: string;

  constructor(store: Store<AppState>, private actions$: Actions) {
    this.store = store;
    this.subs = new Subscription();
    this.layers = [];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.markersArray && this.map && this.markersArray) {
      this.updateMarkers();
    }
    if (changes.mapId && !changes.mapId.firstChange) {
      this.reinitializeMap();
    }
  }

  ngAfterViewInit() {
    // Ensure DOM is ready
    setTimeout(() => {
      this.subs.add(this.store.select(selectTenant).subscribe(tenant => {
        this.tenant = tenant;
        this.tenantName = tenant.unique_name;
        if (tenant && !this.initiated) {
          this.initializeMap();
        }
      }));
      this.setupMapSubscriptions();
    }, 0);

    this.subs.add(
      this.actions$
        .pipe(ofType<UserClickedCommentMap>(EMapActions.UserClickedCommentMap))
        .subscribe(action => {
          if (this.mapMarkersCluster) {
            const layers = this.mapMarkersCluster.getLayers();
            if (layers.length > 0) {
              const selectedMap = layers.filter((item: any) => {
                return item.feature.properties.id == action.payload;
              });
              this.mapMarkerReportClick({ target: selectedMap[0] });
            }
          }
        })
    );

    this.subs.add(
      this.actions$
        .pipe(
          ofType<SimulateUserClickedMap>(EMapActions.SimulateUserClickedMap)
        )
        .subscribe(action => {
          if (!action.payload || (action.payload && !action.payload.actual)) {
            if (this.userCursorMarker) {
              this.userCursorMarker.removeFrom(this.map);
              this.userCursorProperties = null;
            }
            return;
          }
          const { lat, lon, zoom } = action.payload.actual;
          const tempMarker = this.addMarkerToMap(
            { lat, lon },
            this.map,
            mapIcons.userCursorIcon
          );
          const boundingRect = tempMarker.getElement().getBoundingClientRect();
          const [offsetLeft, offsetTop] = mapIcons.userCursorIcon.options
            .iconAnchor as [number, number];
          const left = boundingRect.left + offsetLeft / 2;
          const top = boundingRect.top + offsetTop;
          tempMarker.removeFrom(this.map);
          const ele = document.elementFromPoint(left, top);
          if (ele) {
            happen.click(ele, {
              type: 'click',
              clientX: left,
              clientY: top
            });
          }
        })
    );
  }

  ngOnDestroy() {
    this.cleanupMap();
    this.subs.unsubscribe();
  }

  private buildMapMarker(marker: Marker, icon: L.Icon<any> = null): L.Marker {
    if (!icon) {
      if (marker.parent && marker.parent.color && marker.parent.icon)
        icon = mapIcons.customIcon(marker.parent);
      else if (marker.parent && (marker.parent.color || marker.parent.icon))
        icon = mapIcons.customIcon(marker.parent);
      else icon = mapIcons.defaultIcon(marker.type);
    }
    const m = L.marker({ lat: marker.lat, lng: marker.lon }, { icon: icon });
    m.feature = { type: null, geometry: null, properties: marker.parent };
    return m;
  }

  private addMarkerToMap(
    marker: Marker,
    map: L.Map,
    icon: L.Icon<any> = undefined
  ): L.Marker {
    let showMarker = null;
    if (this.isMapClick || marker) {
      showMarker = this.buildMapMarker(marker, icon);
      showMarker.addTo(map);
    }
    this.isMapClick = false;
    return showMarker;
  }

  private buildMap() {

    let mapOptions = {
      center: MAP_INITIAL_CENTER,
      zoom: MAP_INITIAL_ZOOM,
      zoomControl: false,
      scrollWheelZoom: false,
      zoomSnap: 0.05
    };

    this.map = L.map(this.mapId, mapOptions);
    this.map.addControl(L.control.zoom({ position: MAP_ZOOM_POSITION }));
    L.tileLayer(MAP_URL_TEMPLATE, {
      maxZoom: MAP_ZOOM_MAX,
      attribution: MAP_ATTRIBUTION
    }).addTo(this.map);
    if (this.mapId === VIEW_MAP_ID) {
      this.mapLayerControl = L.control
        .layers(null, null, { position: MAP_LAYER_POSITION, hideSingleBase: false })
        .addTo(this.map);
    }
  }

  private initMap() {
    this.subs.add(
      this.store.select(selectMap).subscribe(map => {
        if (!map || this.initiated || !this.map) return;
        let defaultZoom = map.defaults.zoom;
        if (this.mapId === MAP_VIEW_DETAIL || this.mapId === MOBILE_MAP_ID) {
          defaultZoom = (map.defaults.zoom - ((this.mapId === MOBILE_MAP_ID) ? 0.90 : 2));
          this.map.setView([(map.defaults.lat + LAT_DEVIATION), (map.defaults.lon - LON_DEVIATION)], defaultZoom);
        }
        else if (this.mapId === MAP_VIEW_GET_ALERTS) {
          defaultZoom = 7;
          this.map.setView([map.defaults.lat, map.defaults.lon], defaultZoom);
        } else {
          defaultZoom = (window.innerWidth > DEFAULT_WIDTH) ? (defaultZoom + 0.50) : defaultZoom;
          this.map.setView([map.defaults.lat, map.defaults.lon], defaultZoom);
        }
        this.initiated = true;
        this.initialZoom = defaultZoom;
        setTimeout(() => {
          if (this.map) {
            this.map.invalidateSize();
          }
        }, 100);
      })
    );
    this.subs.add(
      this.actions$
        .pipe(ofType<SetMapDefaults>(EMapActions.SetMapDefaults))
        .subscribe(actions => {
          if (!actions.payload || this.initiated || !this.map) return;
          this.map.setView(
            [actions.payload.defaults.lat, actions.payload.defaults.lon],
            actions.payload.defaults.zoom
          );
          this.initiated = true;
          setTimeout(() => {
            if (this.map) {
              this.map.invalidateSize();
            }
          }, 100);
        })
    );
  }

  private listenToMapEvents() {

    this.ListenerHandle = (event: L.LeafletMouseEvent) => {
      this.unclickMapMarker();
      if (this.userCursorMarker && this.isMapClick) {
        this.userCursorMarker.removeFrom(this.map);
      }
      if (this.userCanClick && this.isMapClick) {
        this.userCursorMarker = this.addMarkerToMap(
          { lat: event.latlng.lat, lon: event.latlng.lng },
          this.map,
          mapIcons.userCursorIcon
        );
        // if (this.userCursorProperties) {
        //   this.userCursorProperties['Departamen'] = (this.userCursorProperties.DEP) ? DepartmentList[this.userCursorProperties.DEP] : null;
        // }
        this.store.dispatch(
          new UserClickedMap({
            actual: {
              lat: event.latlng.lat,
              lon: event.latlng.lng,
              properties: this.userCursorProperties || null
            }
          })
        );
      }
    };
    if (!this.disabled) {
      this.map.addEventListener('click', this.ListenerHandle, true);
    }
  }

  private addShapefiles() {

    let queue: Promise<any>[] = this.tenant.map_shapefiles.map.shapeFiles.map(
      sf => shp(sf.uri) as Promise<any>
    );



    const configs = this.tenant.map_shapefiles.map.shapeFiles.reduce(
      (cfgs, sh) => [...cfgs, ...sh.config],
      []
    );
    let regionGroupBy;

    Promise.all(queue).then(data => {
      let regionList = [];
      const levels = this.tenant.map_shapefiles.map.levels;
      if(this.tenantName === "peru") {
              const customOrder = ["Department2","Clan2","District2"];
              data[0].sort((a, b) => {
              const indexA = customOrder.indexOf(a.fileName);
              const indexB = customOrder.indexOf(b.fileName);

              if (indexA === -1) return 1; // If filename not in customOrder, move it to the end.
              if (indexB === -1) return -1; // If filename not in customOrder, move it to the end.

              return indexA - indexB;
            });
          }
      data.forEach((d) => {
        d.forEach((d1: GeoJsonObject, index) => {
          const config = configs.find(c => c.fileName === d1.fileName) || {
            base: false,
            selected: false,
            options: null,
            listenForClick: null,
            levelsMap: null
          };
          if (index === (levels)) {
            if (this.mapId === VIEW_MAP_ID) {
              if (d1.features) {
                d1.features.map((region) => {
                  let obj = {};
                  if (config && config.levelsMap != null) {
                    config.levelsMap.forEach((element) => {
                      obj[element.name] = region.properties[element.nameShapeFile] || '';
                    });
                    regionList.push(obj);
                  }
                });
              }
              regionGroupBy = lodash.groupBy(regionList, 'department');
              this.store.dispatch(new SetMapRegion({ 'regions': regionGroupBy }));
              regionList = null;
            }
          }




          let layer = L.geoJSON(d1 as any, config.options);
          if (config.options !== null && this.mapId === VIEW_MAP_ID) {
            const name = ((config.altName || d1.fileName) == "Clan") ? "Régions" : "Districts";
            this.mapLayerControl.addOverlay(
              layer,
              (config.altName || d1.fileName)
            );
          }

          this.markerHandler = (event: L.LeafletMouseEvent) => {
            this.isMapClick = true;
            if (config.listenForClick.dispatchProperties)
              this.userCursorProperties = {
                ...this.userCursorProperties,
                ...event.layer.feature.properties
              };
          };

          if (config.listenForClick && config.listenForClick.enabled) {
            layer.addEventListener('click', this.markerHandler, true);
          }

          const flag = (this.mapId === CREATE_MAP_ID) ? ((config.base === true) ? true : false) : config.selected;
          if (flag) {
            layer.addTo(this.map);
          }
        });
      });

    }).catch((error) => {
      console.log(error);
    });
  }

  private handleAuthorization() {
    this.subs.add(
      AuthHelpers.User.HasUserPermission(
        this.store,
        Permission.CreateReport
      ).subscribe(can => {
        if (this.userCanClick !== can && this.userCursorMarker)
          this.userCursorMarker.removeFrom(this.map);
        this.userCanClick = true;
      })
    );
  }

  private unclickMapMarker() {
    if (!this.clickedMapMarker) return;
    this.clickedMapMarker.setIcon(
      mapIcons.customIcon(this.clickedMapMarker.feature.properties)
    );
  }

  private mapMarkerReportClick(event) {
    this.unclickMapMarker();
    if (this.userCursorMarker) {
      this.userCursorMarker.removeFrom(this.map);
    }

    let marker = event.target as L.Marker;
    // marker.setIcon(mapIcons.clickedIcon);
    const originalColor = marker.feature?.properties?.color || 'rgb(255, 164, 51)';
    const clickedIconResult = mapIcons.clickedIcon(originalColor);
    marker.setIcon(L.divIcon(clickedIconResult.options));
    this.clickedMapMarker = marker;

    // Ensure the feature object and properties exist
    const feature = this.clickedMapMarker.feature || { properties: {} };
    const properties = feature.properties;

    // Create a new properties object to avoid modifying read-only properties
    const newProperties = {
      ...properties,
      icon: properties.icon || environment.defaults.tagIcon,
      color: properties.color || environment.defaults.tagColor,
    };

    // Assign the new properties object back to the feature properties
    this.clickedMapMarker.feature.properties = newProperties;

    const parent = newProperties || null;
    this.store.dispatch(
      new UserClickedMarker({
        lat: marker.getLatLng().lat,
        lon: marker.getLatLng().lng,
        parent: { id: parent.id || null }
      })
    );

    marker = null;
  }

  private initializeMap() {
    if (!this.map) {
      this.buildMap();
    }
    this.initMap();
    this.listenToMapEvents();
    this.addShapefiles();
    this.handleAuthorization();
    
    // Handle mobile map sizing
    if (this.mapId === MOBILE_MAP_ID) {
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 500);
    }
  }

  private reinitializeMap() {
    this.cleanupMap();
    this.initiated = false;
    setTimeout(() => {
      if (this.tenant) {
        this.initializeMap();
      }
    }, 200);
  }

  private cleanupMap() {
    if (this.layerGroup) {
      this.layerGroup.clearLayers();
    }
    if (this.ListenerHandle) {
      this.ListenerHandle = null;
    }
    if (this.markerHandler) {
      this.markerHandler = null;
    }
    this.layers.forEach((layer) => {
      if (this.map && layer) {
        this.map.removeLayer(layer);
      }
    });
    if (this.map) {
      this.map.removeEventListener('click');
      this.map.remove();
      this.map.off();
      this.map = null;
    }
    this.layers = [];
  }

  private updateMarkers() {
    if (!this.map) return;
    
    if (this.mapMarkersCluster) {
      this.mapMarkersCluster.removeFrom(this.map);
    }
    
    if (this.markersArray && this.markersArray.length > 0) {
      const mapMarkers = this.markersArray.map(m => this.buildMapMarker(m));
      mapMarkers.forEach(m =>
        m.addEventListener('click', event => {
          this.mapMarkerReportClick(event);
        })
      );
      this.mapMarkersCluster = new L.MarkerClusterGroup();
      this.mapMarkersCluster.addLayer(L.layerGroup(mapMarkers));
      this.map.addLayer(this.mapMarkersCluster);
    }
  }

  private setupMapSubscriptions() {
    this.subs.add(
      this.store.select(selectMap).subscribe(map => {
        if (!map || !this.map) return;

        if (this.userCanClick && !this.userCursorMarker) {
          let tempLatLon;
          if (this.marker && this.marker.lat && this.marker.lon) {
            tempLatLon = { lat: this.marker.lat, lon: this.marker.lon };
          } else if (map.actual && map.actual.lat && map.actual.lon) {
            tempLatLon = { lat: map.actual.lat, lon: map.actual.lon };
          }
          if (tempLatLon) {
            this.userCursorMarker = this.addMarkerToMap(
              tempLatLon,
              this.map,
              mapIcons.userCursorIcon
            );
          }
        }

        if (this.getMarkersFromStore && map.markers && !this.markersArray) {
          if (!lodash.isEqual(map.markers, this.markers)) {
            this.markers = map.markers;
            this.updateMarkersFromStore(map.markers);
          }
        }
      })
    );
  }

  private updateMarkersFromStore(markers: Marker[]) {
    if (!this.map) return;
    
    if (this.mapMarkersCluster) {
      this.mapMarkersCluster.removeFrom(this.map);
    }
    
    if (markers && markers.length > 0) {
      const mapMarkers = markers.map(m => this.buildMapMarker(m));
      mapMarkers.forEach(m =>
        m.addEventListener('click', event => {
          this.mapMarkerReportClick(event);
        })
      );
      this.mapMarkersCluster = new L.MarkerClusterGroup();
      this.mapMarkersCluster.addLayer(L.layerGroup(mapMarkers));
      this.map.addLayer(this.mapMarkersCluster);
    }
  }

}
